import { html } from 'lit';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { customElement, property, query, state } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { RemediationDefaultAction, SizeFilter } from '@nirvana/shared/dtos/types/policies/common';
import { styles } from '../view/styles';
import { TextHighlightMixin } from '../../../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import { MAX_FILTER_SIZE, MIN_FILTER_SIZE } from '../../../../../utils/uiSettings/sandbox/sandbox.values';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { FormFieldInput } from '../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { toggleDisabled } from '../../../../../../../../utils/htmlElement.utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../ui/gzCheckbox';

export class GzFileSizesWebComponent extends FormFieldInput<SizeFilter> {
  @property({ type: Object, attribute: true, reflect: true })
  public sizeFilterLabel: { label: string; translatedLabel: string } = {
    label: '',
    translatedLabel: '',
  };

  @property({ type: Array, attribute: true, reflect: true })
  public defaultActionOptions: RemediationDefaultAction[] = [];

  @state()
  private value: SizeFilter;

  /**
   * Subject to emit the valid state of the port section.
   */
  private validSubject: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  /**
   * Observable to get the valid state of the port section.
   */
  public valid$: Observable<boolean | undefined> = this.validSubject.pipe(distinctUntilChanged());

  @query('#size-filter-checkbox-form-field')
  private sizeFilterCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#size-filter-min-input-form-field')
  private sizeFilterMinFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number | undefined>;
  @query('#size-filter-max-input-form-field')
  private sizeFilterMaxFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number | undefined>;

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.setValidators();
  }

  protected render() {
    this.updateComplete.then(() => {
      this.sizeFilterCheckboxFormField.value = this.value?.enabled ?? false;
      this.sizeFilterMinFormField.value = this.value?.fileMinSizeKB ?? undefined;
      this.sizeFilterMaxFormField.value = this.value?.fileMaxSizeMB ?? undefined;
    });

    return html`
      <div class="checkbox-with-input">
        <gz-form-field class="label-mw-250 checkbox-width" id="size-filter-checkbox-form-field">
          <gz-checkbox
            searchable-label="${this.sizeFilterLabel.label}"
            label="${this.sizeFilterLabel.translatedLabel + ':'}"
            gz-form-input
            @change="${this.onSizeFilterCheckboxChange.bind(this)}"
          ></gz-checkbox>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field class="label-mw-250" id="size-filter-min-input-form-field">
          <gz-input-number
            gz-form-input
            .inputWidth="${InputWidthValues.MEDIUM}"
            .unitSuffix="${'KB'}"
            .min="${MIN_FILTER_SIZE}"
            .max="${MAX_FILTER_SIZE * 1024}"
            @change="${this.onSizeFilterChange.bind(this)}"
          ></gz-input-number>
        </gz-form-field>

        <gz-text id="maxSizeLabel" fontSize="14" fontWeight="400"
          >${translateFn('Maximum size for submitted objects') + ':'}</gz-text
        >

        <gz-form-field class="label-mw-250" id="size-filter-max-input-form-field">
          <gz-input-number
            gz-form-input
            .inputWidth="${InputWidthValues.MEDIUM}"
            .unitSuffix="${'MB'}"
            .min="${MIN_FILTER_SIZE}"
            .max="${MAX_FILTER_SIZE}"
            @change="${this.onSizeFilterChange.bind(this)}"
          ></gz-input-number>
        </gz-form-field>
      </div>
    `;
  }

  private onSizeFilterCheckboxChange(event: CustomEvent) {
    toggleDisabled(this.sizeFilterMinFormField, !event.detail.checked);
    toggleDisabled(this.sizeFilterMaxFormField, !event.detail.checked);
  }

  private onSizeFilterChange(event: CustomEvent) {
    this.sizeFilterMinFormField.validate(true);
    this.sizeFilterMaxFormField.validate(true);
  }

  private setValidators() {
    this.sizeFilterMinFormField.Validators = [
      CustomizableValidator(value => this.validateSizeFilters(value, this.sizeFilterMaxFormField.value)),
    ];

    this.sizeFilterMaxFormField.Validators = [
      CustomizableValidator(value => this.validateSizeFilters(this.sizeFilterMinFormField.value, value)),
    ];
  }

  private validateSizeFilters(minValue: number | undefined, maxValue: number | undefined): ValidationResult {
    if (minValue === undefined || maxValue === undefined) {
      return { valid: true };
    }

    if (minValue >= maxValue * 1024) {
      this.validSubject.next(false);
      return {
        valid: false,
        error: translateFn('The minimum size must be less than the maximum size.'),
      };
    }

    this.validSubject.next(true);
    return { valid: true };
  }

  getFormValue(): SizeFilter {
    return {
      enabled: this.sizeFilterCheckboxFormField.value,
      fileMinSizeKB: this.sizeFilterMinFormField.value || 0,
      fileMaxSizeMB: this.sizeFilterMaxFormField.value || 0,
    };
  }

  setFormValue(value: SizeFilter): void {
    this.reset();
    this.value = value;
  }

  private reset() {
    this.sizeFilterMinFormField.value = undefined;
    this.sizeFilterMaxFormField.value = undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-policy-panel-sandbox-file-sizes': GzFileSizesWebComponent;
  }
}

/**
 * This wrapper is necessary to connect the web component to the store.
 */
@customElement('gz-policy-panel-sandbox-file-sizes')
export class GzFileSizesWrapperWebComponent extends TextHighlightMixin(connect(store)(GzFileSizesWebComponent)) {
  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);
    this.searchString = accordionsSearchApp.searchString;
  }
}
